import {
  Device,
  hasGenericStatistics,
  hasMcuStatistics,
} from "@services/devices/types";
import {
  GraphType,
  MetaData,
  MonitoringGraphData,
  thresholdFields,
} from "@services/monitoring/types";

const MAX_Y = {
  [GraphType.Cpu]: 100,
  [GraphType.Ping_Latency]: 500,
  [GraphType.Memory_In_Use]: 32,
  [GraphType.Port_Usage_Video]: 300,
  [GraphType.Packet_Loss]: 10,
  [GraphType.Packet_Loss_Percentage_Content]: 10,
  [GraphType.Jitter_Audio]: 100,
  [GraphType.Jitter_Video]: 100,
  [GraphType.Jitter_Content]: 100,
  [GraphType.Frame_Rate]: 60,
  [GraphType.Frame_Rate_Content]: 60,
  [GraphType.Call_Rate]: 10000,
  [GraphType.Bit_Rate_Content]: 10000,
};

const isNotNull = (value: number | null): value is number => value !== null;

const getDatasetYMax = (dataset: NonNullable<MonitoringGraphData["data"]>) => {
  const yValues = dataset.map((point) => point[1]).filter(isNotNull);

  if (yValues.length === 0) return null;

  return Math.max(...yValues);
};

const getGraphDataYMax = (data: MonitoringGraphData[]) => {
  const maxYValues = data
    .map((item) => getDatasetYMax(item.data!))
    .filter(isNotNull);

  if (maxYValues.length === 0) return 0;

  return Math.max(...maxYValues);
};

const getMaxThreshold = (data: MonitoringGraphData[]) => {
  return data.reduce((acc, dataset) => {
    const thresholdValues = thresholdFields.map((field) => dataset[field] ?? 0);

    acc = Math.max(acc, ...thresholdValues);

    return acc;
  }, 0);
};

const getMemoryTotal = (device: Device | undefined) => {
  if (device && device.detailedStatuses && hasGenericStatistics(device)) {
    for (let i = 0; i < device.detailedStatuses.length; i++) {
      const stats = device.detailedStatuses[i].statistics;

      if (!stats) continue;

      for (let j = 0; j < stats.length; j++) {
        if (
          stats[j]["@class"] ===
          "com.avispl.symphony.api.dal.dto.monitor.GenericStatistics"
        ) {
          const totalMemory = stats[j].memoryTotal;

          return totalMemory ? Math.ceil(totalMemory) : null;
        }
      }
    }
  }

  return null;
};

const getVideoPortsTotal = (device: Device | undefined) => {
  let ports = device?.details?.totalPorts ?? 0;

  if (!hasMcuStatistics(device)) return ports;

  if (device && device.detailedStatuses) {
    for (let i = 0; i < device.detailedStatuses.length; i++) {
      let stats = device.detailedStatuses[i].statistics;

      if (!stats) continue;

      for (let j = 0; j < stats.length; j++) {
        if (
          stats[j]["@class"] ==
          "com.avispl.symphony.api.dal.dto.monitor.McuStatistics"
        ) {
          ports =
            stats[j].videoMcuPorts?.total ?? MAX_Y[GraphType.Port_Usage_Video];
          break;
        }
      }
    }
  }

  return ports;
};

export const getYMax = (
  data: MonitoringGraphData[],
  device: Device | undefined,
  graphType: GraphType | undefined,
  customGraph: MetaData | undefined
) => {
  const maxThreshold = getMaxThreshold(data);
  const graphDataYMax = getGraphDataYMax(data);

  if (customGraph) {
    return Math.max(graphDataYMax, customGraph.maxValue ?? 0);
  }

  switch (graphType) {
    case GraphType.Cpu:
      return MAX_Y.Cpu;

    case GraphType.Memory_In_Use:
      return getMemoryTotal(device) ?? (maxThreshold || MAX_Y[graphType]);

    case GraphType.Port_Usage_Video:
      return getVideoPortsTotal(device);

    case GraphType.Ping_Latency:
    case GraphType.Packet_Loss:
    case GraphType.Packet_Loss_Percentage_Content:
    case GraphType.Jitter_Audio:
    case GraphType.Jitter_Video:
    case GraphType.Jitter_Content:
    case GraphType.Frame_Rate:
    case GraphType.Frame_Rate_Content:
    case GraphType.Call_Rate:
    case GraphType.Bit_Rate_Content:
      return Math.max(graphDataYMax, maxThreshold, MAX_Y[graphType]);

    default:
      return graphDataYMax;
  }
};
